<template>
    <el-container class="list-page">
        <el-header class="list-page-header centered-list-search-header">
            <h1 class="list-page-title">{{$t('expense.pageTitle')}}</h1>
            <div class="list-page-search">
                <el-input prefix-icon="el-icon-search" v-model="searchContent"
                          :disabled="!contextLoaded"
                          :placeholder="$t('expense.searchPlaceholder')"></el-input>
            </div>
            <div class="expense-header-actions">
                <el-button class="list-page-add-button" type="primary" :disabled="cantAddInvoice" @click.native.prevent="goAddInvoice">
                    <i class="el-icon-plus"></i>
                    <span>{{$t('expense.addExpense')}}</span>
                </el-button>
            </div>
        </el-header>
        <el-main class="list-page-main" v-loading="loading">
            <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <template v-else-if="contextLoaded">
                <div class="invoice-filter-row">
                    <div class="invoice-filter-controls">
                        <el-select class="invoice-client-select" v-model="categoryUUID" clearable
                                   :disabled="loading"
                                   :placeholder="$t('expense.allCategories')" @change="changeExpenseFilter">
                            <el-option v-for="category in categoryOptions" :key="category.innerUUID"
                                       :label="category.name" :value="category.innerUUID"></el-option>
                        </el-select>
                        <el-date-picker class="invoice-date-range" v-model="expenseDateRange" type="daterange"
                                        :disabled="loading"
                                        unlink-panels :start-placeholder="$t('invoice.filterStartDate')"
                                        :end-placeholder="$t('invoice.filterEndDate')"
                                        @change="changeExpenseFilter"></el-date-picker>
                    </div>
                    <el-select class="invoice-year-select" v-model="selectedYear" :disabled="loading || savingSettings"
                               @change="changeYearFilter">
                        <el-option v-for="yearOption in yearOptions" :key="yearOption.key"
                                   :label="yearOption.label" :value="yearOption.value"></el-option>
                    </el-select>
                </div>
                <el-alert v-if="categoryLoadError !== ''" :title="categoryLoadError" type="error"
                          :closable="false" show-icon></el-alert>
                <div v-if="!loading && totalCount === 0" class="expense-empty">
                    <i class="el-icon-wallet"></i>
                    <p>{{$t('common.noData')}}</p>
                </div>
                <section v-for="section in sections" :key="section.key" class="expense-section">
                    <div class="expense-section-title">
                        <span>{{section.title}} ({{section.expenses.length}})</span>
                        <span>{{formatSectionTotal(section.total)}}</span>
                    </div>
                    <div class="list-table-card expense-desktop-table">
                        <el-table class="list-table" @sort-change="sortTable" v-loading="loading" :data="section.expenses"
                                  :default-sort="{prop: orderName, order: orderTpe === 1 ? 'ascending' : 'descending'}"
                                  @row-click='handleRowHandle'>

                            <el-table-column prop="expenseDescription" :label="$t('expense.columnDescription')" min-width="320">
                                <template slot-scope="scope">
                                    <span class="list-description-cell">{{scope.row.expenseDescription}}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="merchant" :label="$t('report.columnMerchant')" min-width="220">
                                <template slot-scope="scope">
                                    <span>{{scope.row.merchant}}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="categoryName" :label="$t('report.columnCategory')" min-width="200">
                                <template slot-scope="scope">
                                    <span>{{scope.row.categoryName}}</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="expenseDate" :label="$t('expense.columnDate')" :formatter="dateFormat" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="160">
                                <template slot="header">
                                    <span class="list-sort-header" :class="{'is-active': orderName === 'expenseDate'}">
                                        {{$t('expense.columnDate')}}
                                        <svg v-if="orderName === 'expenseDate'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path v-if="orderName === 'expenseDate' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                            <path v-else d="M8 18L12 22L16 18"/>
                                            <path d="M12 2V22"/>
                                        </svg>
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="total" :label="$t('expense.columnTotal')" :formatter="amountFormat" sortable="custom" :sort-orders="['descending', 'ascending']" min-width="140" align="right" header-align="right">
                                <template slot="header">
                                    <span class="list-sort-header" :class="{'is-active': orderName === 'total'}">
                                        {{$t('expense.columnTotal')}}
                                        <svg v-if="orderName === 'total'" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path v-if="orderName === 'total' && orderTpe === 1" d="M8 6L12 2L16 6"/>
                                            <path v-else d="M8 18L12 22L16 18"/>
                                            <path d="M12 2V22"/>
                                        </svg>
                                    </span>
                                </template>
                            </el-table-column>

                        </el-table>
                    </div>
                    <div class="expense-mobile-list">
                        <button v-for="expense in section.expenses" :key="expense.innerUUID" type="button"
                                class="expense-mobile-card" @click="openExpense(expense, $event)">
                            <span class="expense-mobile-heading">
                                <strong :title="expense.expenseDescription">{{expense.expenseDescription}}</strong>
                                <strong>{{amountFormat(expense, {property: 'total'})}}</strong>
                            </span>
                            <span class="expense-mobile-facts">
                                <span><small>{{$t('report.columnMerchant')}}</small><span :title="expense.merchant">{{expense.merchant}}</span></span>
                                <span><small>{{$t('report.columnCategory')}}</small><span :title="expense.categoryName">{{expense.categoryName}}</span></span>
                                <span><small>{{$t('expense.columnDate')}}</small>{{dateFormat(expense, {property: 'expenseDate'})}}</span>
                            </span>
                        </button>
                    </div>
                </section>
                <div v-if="totalCount > 0" class="list-pagination">
                    <el-pagination
                            @size-change="changeTotalCount"
                            @current-change="changePage"
                            :current-page="currentPage"
                            :page-sizes="[50, 100, 200, 400]"
                            :page-size="pageSize"
                            layout="total, sizes, prev, pager, next, jumper"
                            :total="totalCount">
                    </el-pagination>
                </div>
            </template>
        </el-main>

    </el-container>
</template>

<script>
    import {formatEnglishDate} from '@/utils/time'
    import {timest, toTmpStartOfDayTime} from '@/utils/validate'
    import {debounce} from '@/utils/debounce'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {replaceStoredSetting, syncCompanySelection} from '@/utils/company'
    import {readListPagination, writeListPagination} from '@/utils/listPagination'
    import {readListQueryState, writeListQueryState} from '@/utils/listQueryState'
    import {getBrowserStartOfTodayEpochSecond, getBrowserTimeZone} from '@/utils/browserTimeZone'
    import {
        ALL_EXPENSE_YEARS,
        buildExpenseSections,
        validateExpensePreferences
    } from '@/utils/expenseList'
    import {openListDetail} from '@/utils/listNavigation'

    export default {
        computed: {
            yearOptions() {
                if (!this.contextLoaded) {
                    return [];
                }
                const yearList = [{key: 'all', label: this.$t('invoice.allYears'), value: null}];
                for (const year of this.yearList) {
                    yearList.push({key: String(year), label: String(year), value: year});
                }
                return yearList;
            },
            timePeriodType() {
                return this.selectedYear === null ? 'ALL_TIME' : 'CALENDAR_YEAR';
            },
            sections() {
                if (!this.contextLoaded || this.loadError !== '') {
                    return [];
                }
                return buildExpenseSections(
                    this.tableData,
                    this.settings.expenseGroupBy,
                    this.orderName,
                    this.orderTpe === 1,
                    this.$i18n.locale,
                    this.$t('expense.noCategory')
                );
            }
        },
        methods: {
            restoreListPagination() {
                const pagination = readListPagination('expense');
                if (pagination != null) {
                    this.currentPage = pagination.currentPage;
                    this.pageSize = pagination.pageSize;
                }
            },
            restoreListQueryState() {
                const queryState = readListQueryState('expense');
                if (queryState == null) {
                    return;
                }
                this.currentPage = queryState.currentPage;
                this.pageSize = queryState.pageSize;
                this.searchContent = queryState.searchContent;
                this.categoryUUID = queryState.categoryUUID;
                this.expenseDateRange = queryState.expenseDateRange == null
                    ? null : queryState.expenseDateRange.map(timestamp => new Date(timestamp));
                this.selectedYear = queryState.selectedYear;
                this.orderName = queryState.orderName;
                this.orderTpe = queryState.orderTpe;
            },
            saveListQueryState() {
                writeListQueryState('expense', {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    searchContent: this.searchContent,
                    categoryUUID: this.categoryUUID,
                    expenseDateRange: this.expenseDateRange == null
                        ? null : this.expenseDateRange.map(date => new Date(date).getTime()),
                    selectedYear: this.selectedYear,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe
                });
            },
            saveListPagination() {
                writeListPagination('expense', this.currentPage, this.pageSize);
            },
            applyExpensePreferences(setting) {
                validateExpensePreferences(setting);
                this.settings = {
                    expenseFilterYear: setting.expenseFilterYear,
                    expenseGroupBy: setting.expenseGroupBy,
                    expenseSortType: setting.expenseSortType,
                    expenseSortAscending: setting.expenseSortAscending
                };
                this.selectedYear = setting.expenseFilterYear === ALL_EXPENSE_YEARS
                    ? null
                    : Number(setting.expenseFilterYear);
                this.orderName = setting.expenseSortType === 0 ? 'categoryName' : 'expenseDate';
                this.orderTpe = setting.expenseSortAscending === 1 ? 1 : 2;
            },
            async persistExpensePreferences(preferences) {
                preferences = validateExpensePreferences(preferences);
                const response = await this.$axios.post('update_setting', {
                    params: Object.assign({
                        innerUUID: this.userSetting.innerUUID,
                        companyUUID: this.company.innerUUID,
                        accessDate: timest()
                    }, preferences)
                });
                if (response.data.errorCode !== 0) {
                    throw new Error('Updated expense preferences are invalid');
                }
                this.userSetting = response.data.objectData == null
                    ? Object.assign({}, this.userSetting, preferences)
                    : response.data.objectData;
                validateExpensePreferences(this.userSetting);
                replaceStoredSetting(this.userSetting);
                this.applyExpensePreferences(this.userSetting);
            },
            async changeYearFilter() {
                if (!this.contextLoaded || this.loading || this.savingSettings) {
                    return;
                }
                const previousYear = this.settings.expenseFilterYear;
                const preferences = Object.assign({}, this.settings, {
                    expenseFilterYear: this.selectedYear === null ? ALL_EXPENSE_YEARS : String(this.selectedYear)
                });
                this.savingSettings = true;
                try {
                    await this.persistExpensePreferences(preferences);
                    this.currentPage = 1;
                    this.saveListPagination();
                    await this.queryList();
                } catch (error) {
                    this.selectedYear = previousYear === ALL_EXPENSE_YEARS ? null : Number(previousYear);
                    this.$message.error(error.message);
                } finally {
                    this.savingSettings = false;
                }
            },
            goAddInvoice() {
                if (this.cantAddInvoice || !this.contextLoaded) {
                    return;
                }
                this.$router.push('/createExpense');
            },
            queryList() {
                if (!this.contextLoaded || this.company == null) {
                    return Promise.reject(new Error('Current company is required'));
                }
                const requestSequence = ++this.listRequestSequence;
                let that = this;
                this.loading = true;
                this.loadError = "";
                this.cantAddInvoice = true;
                return this.$axios.post('query_expense_list_user_web', {
                    params: this.buildExpenseListParams()
                }).then(response => {
                    if (requestSequence !== that.listRequestSequence) {
                        return;
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Expense list query failed');
                    }
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    that.tableData = dataList.filter(expense => expense != null
                        && (expense.companyUUID == null || expense.companyUUID === that.company.innerUUID)
                        && Number(expense.syncStatus) !== 1);
                    const totalCount = Number(response.data.totalCount);
                    that.totalCount = Number.isFinite(totalCount) && totalCount >= 0
                        ? Math.floor(totalCount) : that.tableData.length;
                    that.yearList = Array.from(new Set((Array.isArray(response.data.yearList)
                        ? response.data.yearList : []).map(Number).filter(Number.isInteger))).sort((left, right) => right - left);
                    that.totalTypeCount = response.data.totalTypeCount;
                    that.loading = false;
                    that.cantAddInvoice = false;

                }).catch(() => {
                    if (requestSequence === that.listRequestSequence) {
                        that.loading = false;
                        that.cantAddInvoice = false;
                        that.loadError = that.$t('common.loadFailed');
                    }
                })
            },
            buildExpenseListParams() {
                const params = {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    orderName: this.orderName,
                    orderTpe: this.orderTpe,
                    companyUUID: this.company.innerUUID,
                    userWebCommonQuery: this.searchContent,
                    timePeriodType: this.timePeriodType,
                    nowTime: getBrowserStartOfTodayEpochSecond(),
                    timeZone: getBrowserTimeZone()
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                if (this.categoryUUID !== '') {
                    params.categoryUUID = this.categoryUUID;
                }
                if (this.expenseDateRange != null && this.expenseDateRange.length === 2) {
                    params.startExpenseDate = parseInt(toTmpStartOfDayTime(this.expenseDateRange[0]), 10);
                    const endDate = new Date(this.expenseDateRange[1]);
                    endDate.setDate(endDate.getDate() + 1);
                    params.endExpenseDate = parseInt(toTmpStartOfDayTime(endDate), 10);
                }
                return params;
            },
            queryCategoryOptions() {
                this.categoryLoadError = '';
                return this.$axios.post('query_category_list', {
                    params: {
                        appVersion: 700,
                        syncSuccessDate: '0',
                        fromClient: 3,
                        companyUUID: this.company.innerUUID
                    }
                }).then(response => {
                    if (response.data.errorCode !== 0) {
                        throw new Error('Expense category query failed');
                    }
                    const dataList = Array.isArray(response.data.dataList) ? response.data.dataList : [];
                    this.categoryOptions = dataList.filter(category => category != null
                        && (category.companyUUID == null || category.companyUUID === this.company.innerUUID)
                        && Number(category.syncStatus) !== 1);
                }).catch(() => {
                    this.categoryOptions = [];
                    this.categoryLoadError = this.$t('expense.categoryLoadFailed');
                });
            },
            changeExpenseFilter() {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            },
            sortTable(column) {
                if (!column.order) {
                    this.orderName = this.settings.expenseSortType === 0 ? 'categoryName' : 'expenseDate';
                    this.orderTpe = this.settings.expenseSortAscending === 1 ? 1 : 2;
                    this.queryList();
                    return;
                }
                this.orderName = column.prop;
                if (column.order === "ascending") {
                    this.orderTpe = 1
                } else if (column.order === "descending") {
                    this.orderTpe = 2
                }
                this.queryList();
            },
            changePage(val) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.currentPage = val;
                this.saveListPagination();
                this.queryList();
            },
            changeTotalCount(val) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                this.currentPage = 1;
                this.pageSize = val;
                this.saveListPagination();
                this.queryList();
            },
            handleRowHandle(row, column, event) {
                if (!this.contextLoaded || this.loading) {
                    return false;
                }
                this.openExpense(row, event);
            },
            openExpense(row, event) {
                if (!this.contextLoaded || this.loading) {
                    return;
                }
                openListDetail(this.$router, {path: 'updateExpense', query: {innerUUID: row.innerUUID}}, event);
            },
            //时间格式化
            dateFormat: function (row, column) {
                var timestamp = row[column.property] * 1000;
                if (timestamp == undefined) {
                    return "";
                }
                return formatEnglishDate(timestamp);
            },
            //金钱格式化,增加金钱符号和分隔符
            amountFormat: function (row, column) {
                var columnAmount = row[column.property];
                if (columnAmount == undefined) {
                    return "";
                }
                return xiaoshudianfenge(columnAmount, this.userSetting);
            },
            formatSectionTotal(total) {
                return xiaoshudianfenge(total, this.userSetting);
            }

        },
        data() {
            return {
                tableData: [],
                loading: true,
                loadError: "",
                listRequestSequence: 0,
                contextLoaded: false,
                company: null,
                currentPage: 1,
                totalCount: 0,
                pageSize: 50,
                orderName: "expenseDate",
                orderTpe: 2,
                userSetting: null,
                cantAddInvoice: true,
                totalTypeCount: 0,
                searchContent: '',
                categoryUUID: '',
                categoryOptions: [],
                categoryLoadError: '',
                expenseDateRange: null,
                selectedYear: null,
                yearList: [],
                savingSettings: false,
                settings: {
                    expenseFilterYear: ALL_EXPENSE_YEARS,
                    expenseGroupBy: 0,
                    expenseSortType: 1,
                    expenseSortAscending: 0
                },
            }
        },
        async mounted() {
            this.restoreListPagination();
            try {
                const response = await this.$axios.post('query_setting_and_company', {params: {}});
                if (response.data.errorCode !== 0) {
                    throw new Error('Company and setting query failed');
                }
                const selectedData = syncCompanySelection(response.data);
                if (selectedData == null) {
                    throw new Error('Selected company data is missing');
                }
                this.company = selectedData.company;
                this.userSetting = selectedData.setting;
                this.applyExpensePreferences(this.userSetting);
                this.restoreListQueryState();
                this.contextLoaded = true;
                this.queryCategoryOptions();
                await this.queryList();
            } catch {
                this.loading = false;
                this.cantAddInvoice = true;
                this.loadError = this.$t('common.loadFailed');
            }
            this.$watch('searchContent', debounce(() => {
                if (!this.contextLoaded) {
                    return;
                }
                this.currentPage = 1;
                this.saveListPagination();
                this.queryList();
            }, 500));
        },
        beforeRouteLeave(to, from, next) {
            this.saveListQueryState();
            next();
        }
    }
</script>

<style scoped>
    .expense-header-actions { display: flex; justify-content: flex-end; gap: 12px; }
    .expense-section { margin-bottom: 26px; }
    .expense-section-title {
        display: flex; justify-content: space-between; gap: 16px; padding: 0 4px 10px;
        color: #52627c; font-size: var(--responsive-font-14); font-weight: 700;
    }
    .expense-section-title span:first-child { min-width: 0; overflow-wrap: anywhere; }
    .expense-section-title span:last-child { flex: none; font-variant-numeric: tabular-nums; }
    .expense-mobile-list { display: none; }
    .expense-empty { padding: 64px 20px; text-align: center; color: #7c8aa2; }
    .expense-empty i { display: block; margin-bottom: 12px; font-size: 34px; }
    .expense-empty p { margin: 0; font-size: 15px; font-weight: 600; }

    @media (max-width: 760px) {
        .expense-header-actions { justify-content: flex-start; flex-wrap: wrap; }
        .expense-desktop-table { display: none; }
        .expense-mobile-list { display: grid; gap: 14px; }
        .expense-mobile-card {
            width: 100%; padding: 18px; border: 1px solid #e4eaf3; border-radius: 14px;
            background: #fff; box-shadow: 0 8px 24px rgba(41, 62, 96, .07); color: #1d2a3d;
            font: inherit; text-align: left; cursor: pointer;
        }
        .expense-mobile-heading { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 14px; align-items: start; }
        .expense-mobile-heading strong:first-child {
            display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2;
            overflow-wrap: anywhere; line-height: 1.35;
        }
        .expense-mobile-heading strong:last-child { white-space: nowrap; color: #182235; }
        .expense-mobile-facts { display: grid; gap: 10px; margin-top: 16px; }
        .expense-mobile-facts > span { display: grid; grid-template-columns: 82px minmax(0, 1fr); gap: 10px; min-width: 0; }
        .expense-mobile-facts small { color: #8390a5; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
        .expense-mobile-facts > span > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    }
</style>
